import {
  CustomBlockingRulesOptions,
  type IntersectOptionPayload,
  type InventoryAndContentOptionPayload,
  type InventoryAndContentState,
  InventoryContentOption,
  type PublisherInventoryRuleOptions,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { type DisplayContextsType, displayContexts } from '@openx/types/targeting/targeting.ts';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { AnyAction } from '@reduxjs/toolkit';
import { type Dispatch, type SetStateAction, useCallback, useMemo } from 'react';
import { useValidateDomain } from '../../../Domains/hooks/useValidateDomain.ts';
import { INVENTORY_LIMITS } from '../../../shared/InventoryAndContent/constants.ts';
import {
  setComparisonOpValue,
  setPublisherInventoryRules,
} from '../../../shared/InventoryAndContent/inventoryHelpers.ts';
import {
  validateBulkDomains,
  validateBulkInventoryIds,
  validateBulkItemsWithInitialValue,
  validateIfPublisherIdIsUsed,
} from '../../../shared/InventoryAndContent/utils.ts';
import { useKeywordValidation } from '../../../shared/KeywordsInput/useKeywordValidation.ts';
import { publisherInventoryRuleOptionsInitialState } from '../constants.ts';
import { getItemsCount, getPublisherInventoryRulesItemsCount } from '../utils/getItemsCount.ts';
import { isOptionsIdsTypeCheck } from '../utils/isOptionsIdsTypeCheck.ts';
import { preparePublisherInventoryRulesPayload } from '../utils/preparePublisherInventoryRulesPayload.ts';

/**
 * Clamp the requested values to the remaining capacity defined by INVENTORY_LIMITS for
 * the given content option. Options not listed in INVENTORY_LIMITS (e.g. PUBLISHER_ID,
 * SITE, ADUNIT, INSTANCE_HASH, LINE_ITEM_APP_BUNDLE_ID) pass through unchanged.
 */
const capValuesToRemainingLimit = (
  contentOption: InventoryContentOption,
  values: string[],
  currentItemsCount: number
): string[] => {
  const limit = INVENTORY_LIMITS[contentOption];

  if (typeof limit !== 'number') return values;

  const remaining = Math.max(0, limit - currentItemsCount);

  return values.slice(0, remaining);
};

export type UseSetupInventoryAndContentProps = {
  targetingParams: InventoryAndContentState;
  contentOption: InventoryContentOption;
  comparisonType: ComparisonType;
  isBefInstance: boolean;
  dispatchInventory: (action: AnyAction) => void;
  publisherInventoryRuleOptions: PublisherInventoryRuleOptions;
  setPublisherInventoryRuleOptions: Dispatch<SetStateAction<PublisherInventoryRuleOptions>>;
  displayContext?: DisplayContextsType;
};

export const useSetupInventoryAndContent = ({
  dispatchInventory,
  targetingParams,
  contentOption,
  comparisonType,
  isBefInstance,
  publisherInventoryRuleOptions,
  setPublisherInventoryRuleOptions,
  displayContext,
}: UseSetupInventoryAndContentProps) => {
  const inventoryContent = targetingParams[contentOption] as InventoryAndContentOptionPayload | IntersectOptionPayload;

  const isTrafficSettingContext = displayContext === displayContexts.TRAFFIC_SET;

  const comparisonFieldCriteria = useMemo(
    () => (Array.isArray(inventoryContent?.val) ? inventoryContent?.val : inventoryContent?.val?.[comparisonType]),
    [comparisonType, inventoryContent?.val]
  );

  const itemsCount = useMemo(
    () =>
      contentOption === InventoryContentOption.PUBLISHER_INVENTORY_RULES
        ? getPublisherInventoryRulesItemsCount(
            targetingParams[InventoryContentOption.PUBLISHER_INVENTORY_RULES],
            publisherInventoryRuleOptions
          )
        : getItemsCount(inventoryContent?.val),
    [contentOption, inventoryContent?.val, targetingParams, publisherInventoryRuleOptions]
  );

  const { validateBulkKeywords } = useKeywordValidation();
  const validateDomain = useValidateDomain();

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  const addItems = useCallback(
    (values: string[]) => {
      if (contentOption === InventoryContentOption.PUBLISHER_INVENTORY_RULES) {
        const payload = preparePublisherInventoryRulesPayload(
          targetingParams[InventoryContentOption.PUBLISHER_INVENTORY_RULES],
          publisherInventoryRuleOptions,
          values
        );

        dispatchInventory({
          payload,
          type: setPublisherInventoryRules,
        } as unknown as AnyAction);

        setPublisherInventoryRuleOptions(publisherInventoryRuleOptionsInitialState);

        return;
      }

      if (isOptionsIdsTypeCheck(contentOption) || contentOption === InventoryContentOption.KEYWORDS) {
        const valuesToAdd = capValuesToRemainingLimit(contentOption, values, itemsCount);

        if (!valuesToAdd.length) return;

        dispatchInventory({
          payload: isTrafficSettingContext ? [...valuesToAdd] : [...(comparisonFieldCriteria || []), ...valuesToAdd],
          type: setComparisonOpValue[contentOption],
        } as unknown as AnyAction);

        return;
      }

      const valuesToAdd = capValuesToRemainingLimit(contentOption, values, itemsCount);

      if (!valuesToAdd.length) return;

      const currentComparisonData = inventoryContent?.val?.[comparisonType] || [];
      const updatedValues = [...currentComparisonData, ...valuesToAdd];

      dispatchInventory({
        payload: { comparisonType, values: updatedValues },
        type: setComparisonOpValue[contentOption],
      });
    },
    [
      comparisonFieldCriteria,
      comparisonType,
      contentOption,
      dispatchInventory,
      displayContext,
      inventoryContent,
      itemsCount,
      publisherInventoryRuleOptions,
      setPublisherInventoryRuleOptions,
      targetingParams,
      isTrafficSettingContext,
    ]
  );

  const customValidation = useMemo(() => {
    if (contentOption === InventoryContentOption.KEYWORDS) {
      return (values: string[]) =>
        validateBulkKeywords(values, { generalKeywords: targetingParams[InventoryContentOption.KEYWORDS]?.val });
    }

    if (contentOption === InventoryContentOption.PUBLISHER_INVENTORY_RULES) {
      if (publisherInventoryRuleOptions.blockingRuleOption === CustomBlockingRulesOptions.BUNDLE_ID) {
        const existingRuleIndex = targetingParams?.[InventoryContentOption.PUBLISHER_INVENTORY_RULES].findIndex(
          rule =>
            rule.publisher_id === publisherInventoryRuleOptions.publisherID &&
            rule.blockingRuleOption === CustomBlockingRulesOptions.BUNDLE_ID
        );

        const existingRuleBundleIds =
          targetingParams?.[InventoryContentOption.PUBLISHER_INVENTORY_RULES][existingRuleIndex]?.bundle_id;

        return (values: string[]) =>
          validateBulkInventoryIds(
            values,
            {
              val: existingRuleBundleIds,
            },
            InventoryContentOption.PUBLISHER_INVENTORY_RULES
          );
      }

      return (values: string[]) =>
        validateBulkDomains(
          values,
          publisherInventoryRuleOptions,
          targetingParams[InventoryContentOption.PUBLISHER_INVENTORY_RULES],
          validateDomain
        );
    }

    if (contentOption === InventoryContentOption.PUBLISHER_ID) {
      return async (values: string[]) => {
        if (isTrafficSettingContext) {
          return validateBulkItemsWithInitialValue(values, contentOption);
        }

        const blockedPublisherIds = targetingParams?.[InventoryContentOption.PUBLISHER_INVENTORY_RULES].map(
          rule => rule.publisher_id
        );

        const checkIfPublisherIdsAreUsed = blockedPublisherIds.length
          ? await validateBulkInventoryIds(
              values,
              {
                val: blockedPublisherIds,
              } as IntersectOptionPayload,
              contentOption,
              validateIfPublisherIdIsUsed
            )
          : { validItems: values, errors: [] };

        const checkIfPublisherIdsAreValid = await validateBulkInventoryIds(
          values,
          inventoryContent as IntersectOptionPayload,
          contentOption
        );

        const validPublisherIdsSet = new Set(checkIfPublisherIdsAreValid.validItems);
        const commonValidItems = checkIfPublisherIdsAreUsed.validItems.filter(publisherId =>
          validPublisherIdsSet.has(publisherId)
        );

        return {
          errors: [...checkIfPublisherIdsAreUsed.errors, ...checkIfPublisherIdsAreValid.errors],
          validItems: [...new Set(commonValidItems)],
        };
      };
    }

    if (isOptionsIdsTypeCheck(contentOption) && isBefInstance) {
      return (values: string[]) =>
        isTrafficSettingContext
          ? validateBulkItemsWithInitialValue(values, contentOption)
          : validateBulkInventoryIds(values, inventoryContent as IntersectOptionPayload, contentOption);
    }

    return null;
  }, [
    isBefInstance,
    inventoryContent,
    contentOption,
    publisherInventoryRuleOptions,
    validateBulkKeywords,
    validateDomain,
    targetingParams,
    isTrafficSettingContext,
  ]);

  return {
    itemsCount,
    addItems,
    customValidation,
    comparisonFieldCriteria,
  };
};
